import { json } from '@codemirror/lang-json';
import { type Diagnostic, lintGutter, linter } from '@codemirror/lint';
import { type Extension } from '@codemirror/state';
import { oneDark } from '@codemirror/theme-one-dark';
import { EditorView } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import React, { useEffect, useMemo } from 'react';

import { JsonEditorWrapper } from './JsonEditor.styles';

export interface JsonEditorProps {
  value: string;
  onChange?: (value: string) => void;
  height?: string;
  readOnly?: boolean;
  placeholder?: string;
  theme?: 'light' | 'dark';
  onValidate?: (isValid: boolean, error?: string) => void;
  lineWrapping?: boolean;
}

export const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  onChange,
  height = '100%',
  readOnly = false,
  placeholder = 'Enter JSON...',
  theme = 'light',
  onValidate,
  lineWrapping = false,
}) => {
  // JSON linting function
  const jsonLinter = useMemo(() => {
    return linter((view) => {
      const diagnostics: Diagnostic[] = [];
      try {
        JSON.parse(view.state.doc.toString());
        onValidate?.(true);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Invalid JSON';
        onValidate?.(false, message);

        // Try to extract position from error message
        const match = message.match(/position (\d+)/);
        const pos = match && match[1] ? parseInt(match[1], 10) : 0;

        diagnostics.push({
          from: Math.max(0, pos - 1),
          to: Math.min(view.state.doc.length, pos + 1),
          severity: 'error',
          message: message,
        });
      }
      return diagnostics;
    });
  }, [onValidate]);

  const extensions = useMemo((): Extension[] => {
    const exts: Extension[] = [json()];
    if (!readOnly) {
      exts.push(jsonLinter);
      exts.push(lintGutter());
    }
    if (lineWrapping) {
      exts.push(EditorView.lineWrapping);
    }
    return exts;
  }, [jsonLinter, readOnly, lineWrapping]);

  // Validate on initial mount and when value changes
  useEffect(() => {
    if (onValidate && value) {
      try {
        JSON.parse(value);
        onValidate(true);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Invalid JSON';
        onValidate(false, message);
      }
    }
  }, [value, onValidate]);

  return (
    <JsonEditorWrapper>
      <CodeMirror
        value={value}
        onChange={onChange}
        extensions={extensions}
        theme={theme === 'dark' ? oneDark : undefined}
        editable={!readOnly}
        placeholder={placeholder}
        height={height}
        basicSetup={{
          drawSelection: true,
          lineNumbers: true,
          foldGutter: true,
          dropCursor: true,
          allowMultipleSelections: true,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          searchKeymap: true,
          highlightSelectionMatches: true,
        }}
      />
    </JsonEditorWrapper>
  );
};

export default JsonEditor;
